// src/components/Calendar/TradingEconomicsCalendarMobile.jsx
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Button, Offcanvas } from "react-bootstrap";
import { fetchPutResponse } from "../../utilities/ApiUtilities";
import { useTheme } from "../../ThemeProvider/ThemeContext";
import MobileHeader from "../CoTrader/MobileHeader";
import {
  TRADING_ECONOMICS_COUNTRY_OPTIONS as countryOptions,
} from "./TradingEconomicsCountries";
import {
  autoScrollSelectComponents,
  prioritizeSelectedOption,
} from "../Common/SelectMenuScroll";

/* ------------------------------- Date helpers ------------------------------- */
function parseApiUtcToLocalDate(v) {
  if (v == null) return null;
  if (v instanceof Date) return new Date(v.getTime());
  if (typeof v === "number") return new Date(v < 1e12 ? v * 1000 : v);

  let s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}(:\d{2})?/.test(s)) s = s.replace(" ", "T");
  const hasTZ = /[Zz]|[-+]\d{2}:\d{2}$/.test(s);
  if (!hasTZ) s = s + "Z";
  return new Date(s);
}

const fmtMonthYear = (d) =>
  new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(d);

const fmtWeekdayShort = (i) =>
  new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(new Date(2025, 0, 5 + i));

const fmtTime = (input) => {
  const date = input instanceof Date ? input : new Date(input);
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(date);
};

const dateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function startOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  x.setHours(23, 59, 59, 999);
  return x;
}
function buildMonthMatrix(anyDayInMonth) {
  const first = startOfMonth(anyDayInMonth);
  const startIdx = first.getDay();
  const gridStart = addDays(first, -startIdx);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

/* ----------------------------- react-select theme ---------------------------- */
function selectStyles(isDark) {
  return {
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    menu: (base) => ({
      ...base,
      zIndex: 99999,
      borderRadius: "0.75rem",
      backgroundColor: isDark ? "#0f172a" : "#ffffff",
      color: isDark ? "#e5e7eb" : "#111827",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)"}`,
      overflow: "hidden",
    }),
    control: (base, state) => ({
      ...base,
      borderRadius: "0.75rem",
      minHeight: 46,
      backgroundColor: isDark ? "#0f172a" : "#ffffff",
      borderColor: state.isFocused
        ? isDark
          ? "rgba(96,165,250,0.6)"
          : "rgba(13,110,253,.35)"
        : isDark
        ? "rgba(255,255,255,0.12)"
        : "rgba(0,0,0,0.12)",
      boxShadow: "none",
      ":hover": {
        borderColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)",
      },
    }),
    singleValue: (base) => ({ ...base, color: isDark ? "#e5e7eb" : "#111827" }),
    input: (base) => ({ ...base, color: isDark ? "#e5e7eb" : "#111827" }),
    placeholder: (base) => ({ ...base, color: isDark ? "rgba(229,231,235,0.65)" : "rgba(17,24,39,0.55)" }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? isDark
          ? "rgba(59,130,246,0.22)"
          : "rgba(13,110,253,.12)"
        : state.isFocused
        ? isDark
          ? "rgba(255,255,255,0.06)"
          : "rgba(0,0,0,0.04)"
        : isDark
        ? "#0f172a"
        : "#ffffff",
      color: isDark ? "#e5e7eb" : "#111827",
      cursor: "pointer",
    }),
  };
}

export default function TradingEconomicsCalendarMobile() {
  const { isDark } = useTheme();

  // Theme tokens
  const t = useMemo(() => {
    return {
      pageBg: isDark ? "#0b1220" : "#ffffff",
      cardBg: isDark ? "#0f172a" : "#ffffff",
      text: isDark ? "#e5e7eb" : "#111827",
      muted: isDark ? "rgba(229,231,235,0.72)" : "rgba(17,24,39,0.65)",
      border: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)",
      chipBg: isDark ? "rgba(59,130,246,0.18)" : "rgba(25,118,210,.10)",
      dot: isDark ? "rgba(96,165,250,.75)" : "rgba(13,110,253,.6)",
      badgeCompleted: isDark ? "rgba(34,197,94,0.18)" : "rgba(25,135,84,.25)",
      badgePending: isDark ? "rgba(59,130,246,0.20)" : "rgba(13,110,253,.25)",
      badgeOther: isDark ? "rgba(148,163,184,0.18)" : "rgba(108,117,125,.25)",
    };
  }, [isDark]);

  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState("day"); // "month" | "day"
  const [cursorMonth, setCursorMonth] = useState(startOfMonth(today));
  const [selectedDate, setSelectedDate] = useState(startOfDay(today));
  const [country, setCountry] = useState("United States");
  const selectedCountryOption = useMemo(
    () => ({ value: country, label: country }),
    [country],
  );
  const prioritizedCountryOptions = useMemo(
    () => prioritizeSelectedOption(countryOptions, selectedCountryOption),
    [selectedCountryOption],
  );

  const [isLoading, setIsLoading] = useState(false);
  const [eventsByDate, setEventsByDate] = useState({});
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);

  const { fetchStart, fetchEnd } = useMemo(() => {
    if (view === "day") {
      return { fetchStart: startOfDay(selectedDate), fetchEnd: endOfDay(selectedDate) };
    }
    return { fetchStart: startOfMonth(cursorMonth), fetchEnd: endOfMonth(cursorMonth) };
  }, [view, cursorMonth, selectedDate]);

  useEffect(() => {
    let alive = true;
    async function load() {
      setIsLoading(true);
      try {
        const payload = {
          country: country.toLowerCase(),
          start_date: fetchStart.toISOString(),
          end_date: fetchEnd.toISOString(),
        };

        const response = await fetchPutResponse("trading_economics/get_events", payload);
        const { results } = await response.json();
        if (!alive) return;

        const localized = (results || []).map((ev) => {
          const localDate = parseApiUtcToLocalDate(ev.trigger_time);
          return { ...ev, trigger_time_local: localDate, __date_key_local: dateKey(localDate) };
        });

        const grouped = {};
        for (const ev of localized) {
          const key = ev.__date_key_local;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(ev);
        }

        Object.values(grouped).forEach((arr) =>
          arr.sort((a, b) => a.trigger_time_local.getTime() - b.trigger_time_local.getTime())
        );

        setEventsByDate(grouped);
      } catch (e) {
        console.error(e);
        setEventsByDate({});
      } finally {
        if (alive) setIsLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [view, fetchStart, fetchEnd, country]);

  const monthCells = useMemo(() => buildMonthMatrix(cursorMonth), [cursorMonth]);
  const selectedKey = dateKey(selectedDate);
  const dayEvents = eventsByDate[selectedKey] ?? [];

  const weeks = useMemo(() => {
    const rows = [];
    for (let i = 0; i < 6; i++) rows.push(monthCells.slice(i * 7, i * 7 + 7));
    return rows;
  }, [monthCells]);

  function goPrev() {
    if (view === "day") setSelectedDate(addDays(selectedDate, -1));
    else {
      const prev = new Date(cursorMonth);
      prev.setMonth(prev.getMonth() - 1);
      setCursorMonth(startOfMonth(prev));
    }
  }
  function goNext() {
    if (view === "day") setSelectedDate(addDays(selectedDate, 1));
    else {
      const next = new Date(cursorMonth);
      next.setMonth(next.getMonth() + 1);
      setCursorMonth(startOfMonth(next));
    }
  }
  function goToday() {
    setSelectedDate(startOfDay(today));
    setCursorMonth(startOfMonth(today));
    setView("day");
  }
  function openDay(d) {
    setSelectedDate(startOfDay(d));
    setView("day");
  }

  const title = view === "day" ? selectedDate.toDateString() : fmtMonthYear(cursorMonth);

  return (
    <div
      className="d-flex flex-column w-100"
      style={{ height: "100%", minHeight: 0, background: t.pageBg, color: t.text }}
    >
      {/* Sticky toolbar */}
      <MobileHeader />
      <div
        className="border-bottom px-2 py-2"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: t.cardBg,
          borderColor: t.border,
          color: t.text,
        }}
      >
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center gap-2">
            <Button size="sm" variant={isDark ? "outline-light" : "outline-secondary"} onClick={goPrev}>
              ‹
            </Button>
            <Button size="sm" variant={isDark ? "outline-light" : "outline-secondary"} onClick={goToday}>
              Today
            </Button>
            <Button size="sm" variant={isDark ? "outline-light" : "outline-secondary"} onClick={goNext}>
              ›
            </Button>
          </div>

          <Button size="sm" variant={isDark ? "outline-info" : "outline-primary"} onClick={() => setShowFiltersSheet(true)}>
            Filters
          </Button>
        </div>

        <div className="d-flex align-items-center justify-content-between mt-2">
          <div className="fw-semibold text-truncate" style={{ maxWidth: "60%" }}>
            {title}
          </div>

          <div className="btn-group">
            <button
              className={`btn btn-sm ${view === "day" ? "btn-primary" : isDark ? "btn-outline-light" : "btn-outline-primary"}`}
              onClick={() => setView("day")}
            >
              Day
            </button>
            <button
              className={`btn btn-sm ${view === "month" ? "btn-primary" : isDark ? "btn-outline-light" : "btn-outline-primary"}`}
              onClick={() => setView("month")}
            >
              Month
            </button>
          </div>
        </div>

        {isLoading && <div className="small mt-1" style={{ color: t.muted }}>Loading…</div>}
      </div>

      {/* Content scroll area */}
      <div className="flex-grow-1 overflow-auto p-2" style={{ minHeight: 0 }}>
        <div className="rounded-4 border p-2" style={{ background: t.cardBg, borderColor: t.border, color: t.text }}>
          {view === "month" ? (
            <MonthGridMobile
              weeks={weeks}
              cursorMonth={cursorMonth}
              selectedDate={selectedDate}
              eventsByDate={eventsByDate}
              onOpenDay={openDay}
              isDark={isDark}
              t={t}
            />
          ) : (
            <DayViewMobile date={selectedDate} events={dayEvents} isLoading={isLoading} isDark={isDark} t={t} />
          )}
        </div>
      </div>

      {/* Filters Bottom Sheet */}
      <Offcanvas
        show={showFiltersSheet}
        onHide={() => setShowFiltersSheet(false)}
        placement="bottom"
        style={{
          height: "70%",
          backgroundColor: t.cardBg,
          color: t.text,
        }}
      >
        <Offcanvas.Header closeButton closeVariant={isDark ? "white" : undefined}>
          <Offcanvas.Title>Filters</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ background: t.cardBg, color: t.text }}>
          <div className="mb-2 small" style={{ color: t.muted }}>
            Country
          </div>

          <Select
            options={prioritizedCountryOptions}
            value={selectedCountryOption}
            onChange={(selected) => setCountry(selected.value)}
            isSearchable
            menuPortalTarget={document.body}
            components={autoScrollSelectComponents}
            styles={selectStyles(isDark)}
          />

          <div className="d-grid gap-2 mt-4">
            <button className="btn btn-primary rounded-pill" onClick={() => setShowFiltersSheet(false)}>
              Apply
            </button>
            <button
              className={`btn ${isDark ? "btn-outline-light" : "btn-light"} rounded-pill`}
              onClick={() => setShowFiltersSheet(false)}
            >
              Close
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}

/* ---------------------------- Month Grid (Mobile) ---------------------------- */
function MonthGridMobile({ weeks, cursorMonth, selectedDate, eventsByDate, onOpenDay, isDark, t }) {
  const weekdayHeaders = Array.from({ length: 7 }, (_, i) => fmtWeekdayShort(i));

  return (
    <div className="w-100">
      <div className="d-grid" style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {weekdayHeaders.map((w, i) => (
          <div key={i} className="text-center small py-1" style={{ color: t.muted }}>
            {w}
          </div>
        ))}
      </div>

      <div className="d-grid mt-1" style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {weeks.flat().map((day, idx) => {
          const inMonth = day.getMonth() === cursorMonth.getMonth();
          const key = dateKey(day);
          const evCount = (eventsByDate[key] || []).length;

          const isSelected = dateKey(day) === dateKey(selectedDate);
          const isToday = dateKey(day) === dateKey(new Date());

          return (
            <button
              key={idx}
              type="button"
              className="border rounded-3 p-1 text-start"
              onClick={() => onOpenDay(day)}
              style={{
                minHeight: 54,
                background: isSelected ? t.chipBg : t.cardBg,
                opacity: inMonth ? 1 : 0.45,
                borderColor: isToday ? (isDark ? "rgba(96,165,250,0.6)" : "rgba(25,118,210,.6)") : t.border,
                color: t.text,
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <span className="fw-semibold" style={{ fontSize: 12 }}>
                  {day.getDate()}
                </span>
                {evCount > 0 && (
                  <span className="badge text-bg-primary" style={{ fontSize: 10 }}>
                    {evCount}
                  </span>
                )}
              </div>

              {/* tiny dot row */}
              {evCount > 0 && (
                <div className="d-flex gap-1 mt-1 flex-wrap" aria-hidden="true">
                  {Array.from({ length: Math.min(evCount, 4) }).map((_, i) => (
                    <span
                      key={i}
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: t.dot,
                        display: "inline-block",
                      }}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ----------------------------- Day View (Mobile) ----------------------------- */
function DayViewMobile({ date, events = [], isLoading, isDark, t }) {
  return (
    <div className="w-100">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="fw-semibold">Events</div>
        <div className="small" style={{ color: t.muted }}>
          {date.toDateString()}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-4" style={{ color: t.muted }}>
          Loading…
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-4" style={{ color: t.muted }}>
          No events
        </div>
      ) : (
        <div className="list-group">
          {events.map((ev) => (
            <div
              key={ev.id}
              className="list-group-item rounded-3 mb-2"
              style={{
                borderColor: t.border,
                background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                color: t.text,
              }}
            >
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div className="fw-semibold" style={{ fontSize: 14 }}>
                  {ev.title}
                </div>
                <span
                  className="badge"
                  style={{
                    background: badgeBgByStatus(ev.status, t),
                    color: t.text,
                    border: `1px solid ${t.border}`,
                  }}
                >
                  {ev.status}
                </span>
              </div>

              <div className="small mt-1" style={{ color: t.muted }}>
                {fmtTime(ev.trigger_time_local)} • {ev.category} • {ev.country}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function badgeBgByStatus(status, t) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return t.badgeCompleted;
  if (s === "pending") return t.badgePending;
  return t.badgeOther;
}
